/** Dentrist brand colors sampled from src/images/logo/Dentrist-1-01.png */
export const brand = {
  primary: '#1FBAC2',
  primaryHover: '#1BA8B0',
  primaryActive: '#17969D',
  navy: '#00205C',
  navyDeep: '#00153D',
  soft: '#E6F7F9',
  muted: '#B3E4EA',
};

/**
 * Dark surfaces — navy/teal scale (not generic slate).
 * layout → content canvas; chrome → sidebar/header; surface → cards;
 * surfaceRaised → popovers/elevated; surfaceDeep → table heads.
 */
export const surfaces = {
  dark: {
    layout: '#080F1C',
    chrome: '#0C1B36',
    surface: '#111D31',
    surfaceRaised: '#16253D',
    surfaceDeep: '#0A1424',
    border: 'rgba(31, 186, 194, 0.22)',
    borderStrong: 'rgba(31, 186, 194, 0.34)',
    hover: 'rgba(31, 186, 194, 0.10)',
    accentSoft: '#4ECFD6',
    accentMuted: '#7DDEE4',
  },
};

export const shell = {
  light: {
    layoutBg: '#FFFFFF',
    chromeBg: '#F3F8F9',
    contentBg: '#FFFFFF',
    border: '#D6E6E9',
    text: '#00153D',
    textMuted: '#5A6B7D',
    selectedBg: '#E6F7F9',
  },
  dark: {
    layoutBg: surfaces.dark.layout,
    chromeBg: surfaces.dark.chrome,
    contentBg: surfaces.dark.layout,
    border: surfaces.dark.border,
    text: '#E8F1F7',
    textMuted: '#8FA3B8',
    selectedBg: 'rgba(31, 186, 194, 0.18)',
  },
};

/** Sidebar stays navy in both light and dark mode. */
export const sidebar = {
  bg: '#00225F',
  text: '#E8EBF1',
  border: 'rgba(232, 235, 241, 0.14)',
  hoverBg: 'rgba(232, 235, 241, 0.08)',
  selectedBg: 'rgba(31, 186, 194, 0.2)',
};

export const chart = {
  primary: brand.primary,
  secondary: brand.navy,
  /** Cool sage — positive / paid / invoices (harmonizes with teal) */
  success: '#3D9A8B',
  /** Dusty rose — negative / unpaid / expenses (softened vs neon red) */
  danger: '#C45C6A',
  tertiary: '#3D9A8B',
};

/** CSS custom properties for layout chrome (sidebar / header). */
export function getShellCssVars(mode) {
  const s = mode === 'dark' ? shell.dark : shell.light;
  const d = surfaces.dark;
  return {
    '--dentrist-primary': brand.primary,
    '--dentrist-primary-hover': brand.primaryHover,
    '--dentrist-navy': brand.navy,
    '--dentrist-soft': brand.soft,
    '--dentrist-shell-bg': s.chromeBg,
    '--dentrist-layout-bg': s.layoutBg,
    '--dentrist-content-bg': s.contentBg,
    '--dentrist-border': s.border,
    '--dentrist-text': s.text,
    '--dentrist-text-muted': s.textMuted,
    '--dentrist-selected-bg': s.selectedBg,
    '--dentrist-sidebar-bg': sidebar.bg,
    '--dentrist-sidebar-text': sidebar.text,
    '--dentrist-sidebar-border': sidebar.border,
    '--dentrist-sidebar-hover-bg': sidebar.hoverBg,
    '--dentrist-sidebar-selected-bg': sidebar.selectedBg,
    ...(mode === 'dark'
      ? {
          '--dentrist-surface': d.surface,
          '--dentrist-surface-raised': d.surfaceRaised,
          '--dentrist-surface-deep': d.surfaceDeep,
          '--dentrist-hover': d.hover,
          '--dentrist-accent-soft': d.accentSoft,
        }
      : {
          '--dentrist-surface': '#FFFFFF',
          '--dentrist-surface-raised': '#FFFFFF',
          '--dentrist-surface-deep': '#F3F8F9',
          '--dentrist-hover': 'rgba(31, 186, 194, 0.06)',
          '--dentrist-accent-soft': brand.primary,
        }),
  };
}

export function getAntdTheme(mode, { fontFamily } = {}) {
  const isDark = mode === 'dark';
  const s = isDark ? shell.dark : shell.light;
  const d = surfaces.dark;

  return {
    token: {
      colorPrimary: brand.primary,
      colorPrimaryHover: brand.primaryHover,
      colorPrimaryActive: brand.primaryActive,
      colorInfo: brand.primary,
      colorLink: brand.primary,
      colorBgBase: s.layoutBg,
      colorBgContainer: isDark ? d.surface : '#FFFFFF',
      colorBgElevated: isDark ? d.surfaceRaised : '#FFFFFF',
      colorBgLayout: s.layoutBg,
      colorBorder: isDark ? d.border : s.border,
      colorBorderSecondary: isDark ? 'rgba(31, 186, 194, 0.14)' : s.border,
      colorText: isDark ? s.text : s.text,
      colorTextSecondary: s.textMuted,
      colorTextTertiary: isDark ? '#7A90A8' : '#828181',
      colorTextQuaternary: isDark ? '#7A90A8' : '#828181',
      colorPrimaryBg: isDark ? 'rgba(31, 186, 194, 0.14)' : brand.soft,
      borderRadius: 8,
      ...(isDark
        ? {
            colorFillSecondary: 'rgba(31, 186, 194, 0.08)',
            colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
            controlOutlineWidth: 0,
            colorErrorBg: 'rgba(196, 92, 106, 0.18)',
            colorSuccess: chart.success,
            colorError: chart.danger,
          }
        : {}),
      ...(fontFamily ? { fontFamily } : {}),
    },
  };
}
